/**
 * TypeScript definitions for XP System
 *
 * Types for the XP event ledger, user XP summary, level progression,
 * milestones, and gamification constants.
 *
 * @module types/xp
 * @see Database migration: 00013_create_xp_system.sql
 */

// ============================================================================
// Core Enums & Literal Types
// ============================================================================

/** XP event types matching the DB CHECK constraint */
export type XpEventType =
  | 'chat_message'
  | 'srs_review_correct'
  | 'srs_review_incorrect'
  | 'srs_card_mastered'
  | 'new_word_discovered'
  | 'scenario_objective'
  | 'scenario_completed'
  | 'scenario_phase_completed'
  | 'daily_bonus'
  | 'grammar_card_correct'
  | 'grammar_card_incorrect'
  | 'sentence_card_correct'
  | 'sentence_card_incorrect'
  | 'sentence_card_created';

/** Source types for XP events */
export type XpSourceType = 'message' | 'srs_card' | 'scenario_session' | null;

/** Tier names matching the DB function get_user_xp_summary */
export type TierName =
  | 'Beginner'
  | 'Learner'
  | 'Explorer'
  | 'Conversationalist'
  | 'Expert'
  | 'Master';

// ============================================================================
// Database Row Types (snake_case, matching Supabase schema)
// ============================================================================

/** Database row for `xp_events` table */
export interface XpEventRow {
  id: string;
  user_id: string;
  language_code: string;
  event_type: XpEventType;
  base_xp: number;
  multiplier: number;
  final_xp: number;
  source_id: string | null;
  source_type: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

/** Database row for `user_xp` table */
export interface UserXpRow {
  user_id: string;
  language_code: string;
  total_xp: number;
  current_level: number;
  xp_today: number;
  xp_this_week: number;
  last_xp_gained_at: string | null;
  updated_at: string;
}

/** Return shape from `get_user_xp_summary()` RPC */
export interface XpSummaryRow {
  language_code: string;
  total_xp: number;
  current_level: number;
  xp_today: number;
  xp_this_week: number;
  xp_for_current_level: number;
  xp_for_next_level: number;
  xp_progress_in_level: number;
  xp_needed_for_next: number;
  progress_percentage: number;
  tier_name: TierName;
  last_xp_gained_at: string | null;
}

/** Return shape from `get_xp_history()` RPC */
export interface XpHistoryRow {
  day: string;
  daily_xp: number;
  cumulative_xp: number;
  event_count: number;
  level_at_end: number;
}

// ============================================================================
// Application Types (camelCase, used in frontend)
// ============================================================================

/** XP event (frontend representation) */
export interface XpEvent {
  id: string;
  userId: string;
  languageCode: string;
  eventType: XpEventType;
  baseXp: number;
  multiplier: number;
  finalXp: number;
  sourceId: string | null;
  sourceType: string | null;
  metadata: Record<string, unknown>;
  createdAt: Date;
}

/** User XP summary (frontend representation) */
export interface UserXp {
  userId: string;
  languageCode: string;
  totalXp: number;
  currentLevel: number;
  xpToday: number;
  xpThisWeek: number;
  lastXpGainedAt: Date | null;
  updatedAt: Date;
}

/** XP summary for dashboard display */
export interface XpSummary {
  languageCode: string;
  totalXp: number;
  currentLevel: number;
  xpToday: number;
  xpThisWeek: number;
  xpForCurrentLevel: number;
  xpForNextLevel: number;
  xpProgressInLevel: number;
  xpNeededForNext: number;
  progressPercentage: number;
  tierName: TierName;
  lastXpGainedAt: Date | null;
}

/** Single data point for XP history charts */
export interface XpHistoryPoint {
  day: Date;
  dailyXp: number;
  cumulativeXp: number;
  eventCount: number;
  levelAtEnd: number;
}

// ============================================================================
// Mapper Utilities (DB row -> App type)
// ============================================================================

/** Map database xp_events row to XpEvent */
export function mapXpEventRow(row: XpEventRow): XpEvent {
  return {
    id: row.id,
    userId: row.user_id,
    languageCode: row.language_code,
    eventType: row.event_type,
    baseXp: row.base_xp,
    multiplier: row.multiplier,
    finalXp: row.final_xp,
    sourceId: row.source_id,
    sourceType: row.source_type,
    metadata: row.metadata,
    createdAt: new Date(row.created_at),
  };
}

/** Map database user_xp row to UserXp */
export function mapUserXpRow(row: UserXpRow): UserXp {
  return {
    userId: row.user_id,
    languageCode: row.language_code,
    totalXp: row.total_xp,
    currentLevel: row.current_level,
    xpToday: row.xp_today,
    xpThisWeek: row.xp_this_week,
    lastXpGainedAt: row.last_xp_gained_at ? new Date(row.last_xp_gained_at) : null,
    updatedAt: new Date(row.updated_at),
  };
}

/** Map XP summary RPC row to XpSummary */
export function mapXpSummaryRow(row: XpSummaryRow): XpSummary {
  return {
    languageCode: row.language_code,
    totalXp: row.total_xp,
    currentLevel: row.current_level,
    xpToday: row.xp_today,
    xpThisWeek: row.xp_this_week,
    xpForCurrentLevel: row.xp_for_current_level,
    xpForNextLevel: row.xp_for_next_level,
    xpProgressInLevel: row.xp_progress_in_level,
    xpNeededForNext: row.xp_needed_for_next,
    progressPercentage: row.progress_percentage,
    tierName: row.tier_name,
    lastXpGainedAt: row.last_xp_gained_at ? new Date(row.last_xp_gained_at) : null,
  };
}

/** Map XP history RPC row to XpHistoryPoint */
export function mapXpHistoryRow(row: XpHistoryRow): XpHistoryPoint {
  return {
    day: new Date(row.day),
    dailyXp: row.daily_xp,
    cumulativeXp: row.cumulative_xp,
    eventCount: row.event_count,
    levelAtEnd: row.level_at_end,
  };
}

// ============================================================================
// Constants: XP Values per Activity
// ============================================================================

/** Base XP values for each activity type (before multipliers/diminishing returns) */
export const XP_VALUES = {
  chat_message: 2,
  srs_review_correct: 3,
  srs_review_incorrect: 1,
  srs_card_mastered: 15,
  new_word_discovered: 5,
  scenario_objective: 8,
  scenario_completed: 25,
  scenario_phase_completed: 15,
  daily_bonus: 5,
  grammar_card_correct: 8,
  grammar_card_incorrect: 2,
  sentence_card_correct: 10,
  sentence_card_incorrect: 2,
  sentence_card_created: 0, // Notification only — XP awarded on review, not creation
} as const satisfies Record<XpEventType, number>;

/** Human-readable labels for XP event types */
export const XP_EVENT_LABELS: Record<XpEventType, string> = {
  chat_message: 'Chat message',
  srs_review_correct: 'Correct review',
  srs_review_incorrect: 'Review attempt',
  srs_card_mastered: 'Card mastered',
  new_word_discovered: 'New word',
  scenario_objective: 'Objective completed',
  scenario_completed: 'Scenario completed',
  scenario_phase_completed: 'Phase completed',
  daily_bonus: 'Daily bonus',
  grammar_card_correct: 'Grammar correct',
  grammar_card_incorrect: 'Grammar attempt',
  sentence_card_correct: 'Sentence correct',
  sentence_card_incorrect: 'Sentence attempt',
  sentence_card_created: 'Sentence card created',
} as const;

/** Lucide icon names for XP event types */
export const XP_EVENT_ICONS: Record<XpEventType, string> = {
  chat_message: 'MessageSquare',
  srs_review_correct: 'CheckCircle',
  srs_review_incorrect: 'XCircle',
  srs_card_mastered: 'Award',
  new_word_discovered: 'Sparkles',
  scenario_objective: 'Target',
  scenario_completed: 'Trophy',
  scenario_phase_completed: 'Layers',
  daily_bonus: 'Sun',
  grammar_card_correct: 'BookOpen',
  grammar_card_incorrect: 'BookOpen',
  sentence_card_correct: 'Languages',
  sentence_card_incorrect: 'Languages',
  sentence_card_created: 'Languages',
} as const;

// ============================================================================
// Constants: Level & Tier System
// ============================================================================

/** Tier definition with level bracket and color */
export interface TierDefinition {
  name: TierName;
  minLevel: number;
  maxLevel: number;
  color: string;
}

/** All tier brackets, ordered by level */
export const LEVEL_TIERS: readonly TierDefinition[] = [
  { name: 'Beginner', minLevel: 0, maxLevel: 3, color: '#94a3b8' },
  { name: 'Learner', minLevel: 4, maxLevel: 7, color: '#3b82f6' },
  { name: 'Explorer', minLevel: 8, maxLevel: 12, color: '#8b5cf6' },
  { name: 'Conversationalist', minLevel: 13, maxLevel: 18, color: '#f59e0b' },
  { name: 'Expert', minLevel: 19, maxLevel: 25, color: '#10b981' },
  { name: 'Master', minLevel: 26, maxLevel: Infinity, color: '#ef4444' },
] as const;

// ============================================================================
// Constants: Streak Multipliers
// ============================================================================

/** Streak day thresholds and their multipliers */
export const STREAK_MULTIPLIERS = [
  { minDays: 30, multiplier: 1.5 },
  { minDays: 14, multiplier: 1.3 },
  { minDays: 7, multiplier: 1.2 },
  { minDays: 3, multiplier: 1.1 },
  { minDays: 0, multiplier: 1.0 },
] as const;

// ============================================================================
// Constants: Daily Cap / Diminishing Returns
// ============================================================================

/** Diminishing returns tiers for daily XP cap */
export const DAILY_CAP_TIERS = [
  { threshold: 500, rate: 0.25 },
  { threshold: 250, rate: 0.5 },
  { threshold: 100, rate: 0.75 },
  { threshold: 0, rate: 1.0 },
] as const;

// ============================================================================
// Utility Functions: Level & Tier Calculations
// ============================================================================

/**
 * Calculate cumulative XP required to reach a given level.
 * Formula: floor(100 * level^1.5)
 *
 * @example xpRequiredForLevel(10) // 3162
 */
export function xpRequiredForLevel(level: number): number {
  if (level <= 0) return 0;
  return Math.floor(100 * Math.pow(level, 1.5));
}

/**
 * Calculate what level a given total XP corresponds to.
 * Inverse of xpRequiredForLevel with floating-point boundary fix.
 *
 * @example levelFromXp(3162) // 10
 */
export function levelFromXp(totalXp: number): number {
  if (totalXp <= 0) return 0;
  const level = Math.floor(Math.pow(totalXp / 100, 2 / 3));
  // Floating-point boundary fix: check if XP meets next level threshold
  if (totalXp >= xpRequiredForLevel(level + 1)) {
    return level + 1;
  }
  return level;
}

/**
 * Get the tier definition for a given level.
 *
 * @example tierFromLevel(5) // { name: 'Learner', minLevel: 4, maxLevel: 7, color: '#3b82f6' }
 */
export function tierFromLevel(level: number): TierDefinition {
  for (let i = LEVEL_TIERS.length - 1; i >= 0; i--) {
    if (level >= LEVEL_TIERS[i].minLevel) {
      return LEVEL_TIERS[i];
    }
  }
  return LEVEL_TIERS[0];
}

/**
 * Calculate XP progress within the current level.
 * Returns { current, total, percentage } for rendering progress bars.
 *
 * @example xpProgressInLevel(3200)
 * // { currentLevelXp: 3162, nextLevelXp: 3708, progress: 38, remaining: 508 }
 */
export function xpProgressInLevel(totalXp: number): {
  currentLevelXp: number;
  nextLevelXp: number;
  progress: number;
  remaining: number;
} {
  const level = levelFromXp(totalXp);
  const currentLevelXp = xpRequiredForLevel(level);
  const nextLevelXp = xpRequiredForLevel(level + 1);
  const levelRange = nextLevelXp - currentLevelXp;
  const progressInLevel = totalXp - currentLevelXp;

  return {
    currentLevelXp,
    nextLevelXp,
    progress: levelRange > 0 ? Math.round((progressInLevel / levelRange) * 100) : 0,
    remaining: nextLevelXp - totalXp,
  };
}

/**
 * Calculate the diminishing returns rate based on XP earned today.
 * Mirrors the DB function `calculate_diminishing_rate`.
 *
 * @example calculateDiminishingRate(150) // 0.75
 */
export function calculateDiminishingRate(xpToday: number): number {
  for (const tier of DAILY_CAP_TIERS) {
    if (xpToday >= tier.threshold) {
      return tier.rate;
    }
  }
  return 1.0;
}

/**
 * Get the streak multiplier for a given number of consecutive days.
 *
 * @example streakMultiplier(7) // 1.2
 */
export function streakMultiplier(streakDays: number): number {
  for (const tier of STREAK_MULTIPLIERS) {
    if (streakDays >= tier.minDays) {
      return tier.multiplier;
    }
  }
  return 1.0;
}

// ============================================================================
// Milestones
// ============================================================================

/** Context passed to milestone condition checkers */
export interface MilestoneCheckContext {
  level: number;
  totalXp: number;
  streakDays: number;
  messagesCount: number;
  scenariosCompleted: number;
  languageCount: number;
  srsCardsCount: number;
  weeklyXp: number;
}

/** A hardcoded milestone definition */
export interface Milestone {
  id: string;
  title: string;
  description: string;
  icon: string; // lucide icon name
  condition: (ctx: MilestoneCheckContext) => boolean;
}

/** All hardcoded milestones (no DB table needed) */
export const MILESTONES: readonly Milestone[] = [
  {
    id: 'first_words',
    title: 'First Words',
    description: 'Reach Level 1 in any language',
    icon: 'Baby',
    condition: (ctx) => ctx.level >= 1,
  },
  {
    id: 'getting_started',
    title: 'Getting Started',
    description: 'Complete your first scenario',
    icon: 'Play',
    condition: (ctx) => ctx.scenariosCompleted >= 1,
  },
  {
    id: 'chatterbox',
    title: 'Chatterbox',
    description: 'Send 50 chat messages',
    icon: 'MessageSquare',
    condition: (ctx) => ctx.messagesCount >= 50,
  },
  {
    id: 'dedicated_learner',
    title: 'Dedicated Learner',
    description: 'Maintain a 7-day streak',
    icon: 'Flame',
    condition: (ctx) => ctx.streakDays >= 7,
  },
  {
    id: 'vocabulary_builder',
    title: 'Vocabulary Builder',
    description: 'Create 100 flashcards',
    icon: 'BookOpen',
    condition: (ctx) => ctx.srsCardsCount >= 100,
  },
  {
    id: 'polyglot',
    title: 'Polyglot',
    description: 'Earn XP in 2 or more languages',
    icon: 'Globe',
    condition: (ctx) => ctx.languageCount >= 2,
  },
  {
    id: 'scenario_master',
    title: 'Scenario Master',
    description: 'Complete 10 scenarios',
    icon: 'Trophy',
    condition: (ctx) => ctx.scenariosCompleted >= 10,
  },
  {
    id: 'week_warrior',
    title: 'Week Warrior',
    description: 'Earn 200+ XP in one week',
    icon: 'Swords',
    condition: (ctx) => ctx.weeklyXp >= 200,
  },
  {
    id: 'level_10_club',
    title: 'Level 10 Club',
    description: 'Reach Level 10 in any language',
    icon: 'Star',
    condition: (ctx) => ctx.level >= 10,
  },
  {
    id: 'century',
    title: 'Century',
    description: 'Maintain a 100-day streak',
    icon: 'Crown',
    condition: (ctx) => ctx.streakDays >= 100,
  },
] as const;

// ============================================================================
// Type Guards
// ============================================================================

/** Check if a string is a valid XP event type */
export function isValidXpEventType(type: string): type is XpEventType {
  return type in XP_VALUES;
}

/** Check if a string is a valid tier name */
export function isValidTierName(name: string): name is TierName {
  return LEVEL_TIERS.some((t) => t.name === name);
}
